// Cycle counts at a CM: staff type in what the CM counted, and it posts as it's entered (Mitch, 2026-11-03: "it's a bit
// annoying that I have to approve it. I already went through the process"): a count move per part against the book as
// of the count. When something recorded around the count needs placing (a shipment on its way, output reported after
// the count time), the same dialog asks before it posts. Big differences are flagged to count again, never blocked.
// The lines are the usual table (DataTable): counts typed into the Counted cells; a saved count's differences.
import { ClipboardCheck } from "lucide-react";
import { useMemo, useState } from "react";
import { type CmSummary, type CountDecision, countAgain, reviewCount } from "../../../../shared/consignment.ts";
import type { CmCount, Item } from "../../../../shared/db.gen.ts";
import { ago, money, personName, qty } from "../../lib/format.ts";
import { can, mutateWithResult } from "../../lib/mutate.ts";
import { useRows } from "../../lib/sync.ts";
import { Badge, Button, cx, Input, Select, Thumb } from "../../ui/controls.tsx";
import { type Column, DataTable } from "../../ui/DataTable.tsx";
import { toast } from "../../ui/toast.tsx";
import { ItemPicker, Modal, parseQty } from "./parts.tsx";

/** A datetime-local value for now, in this browser's time zone. */
const localNow = () => {
  const d = new Date();
  return new Date(d.getTime() - d.getTimezoneOffset() * 60_000).toISOString().slice(0, 16);
};

type Row = { item: Item; book: number; inShopify: boolean };
type EntryRow = Row & { id: string; listed: number };

export function CountDialog({ cm, onClose }: { cm: CmSummary; onClose: () => void }) {
  const items = useRows("item");
  const itemById = useMemo(() => new Map(items.map((i) => [i.id, i])), [items]);
  const [rows, setRows] = useState<Row[]>(() =>
    cm.stock.flatMap((l) => {
      const item = itemById.get(l.itemId);
      return item && item.kind !== "service" && (l.onHand !== 0 || l.inTransit !== 0) ? [{ item, book: l.onHand - l.inTransit, inShopify: l.inShopify }] : [];
    }),
  );
  const [counted, setCounted] = useState<Record<string, string>>({});
  const [at, setAt] = useState(localNow);
  const [build, setBuild] = useState("");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const [id] = useState(() => crypto.randomUUID());
  const builds = cm.orders.filter((o) => o.output > 0);
  // Saved but with something around the count to place first: the same dialog asks, then posts it.
  const [asking, setAsking] = useState(false);
  const saved = useRows("cm_count").find((c) => c.id === id);

  // The lines: the usual table, each count typed into its Counted cell (Enter saves and moves down).
  const [slot, setSlot] = useState<HTMLDivElement | null>(null); // Table views --- in the toolbar
  const entryRows = useMemo<EntryRow[]>(() => rows.map((r, i) => ({ ...r, id: r.item.id, listed: i })), [rows]);
  const diffOf = (r: Row) => {
    const n = parseQty(counted[r.item.id] ?? "");
    return n == null ? null : n - r.book;
  };
  const entryColumns: Column<EntryRow>[] = [
    { key: "photo", header: "", width: "40px", value: () => null, cell: (r) => <Thumb src={r.item.photo_url} size={24} /> },
    { key: "sku", header: "SKU", width: "130px", value: (r) => r.item.sku, cell: (r) => <span className="truncate font-mono text-[12px]">{r.item.sku}</span> },
    {
      key: "name",
      header: "Part",
      width: "minmax(160px,1fr)",
      value: (r) => r.item.name,
      cell: (r) => (
        <span className="flex min-w-0 items-center gap-1.5">
          <span className="truncate">{r.item.name}</span>
          {r.inShopify && <Badge>Shopify</Badge>}
        </span>
      ),
    },
    {
      key: "book",
      header: "Book now",
      width: "100px",
      align: "right",
      value: (r) => r.book,
      cell: (r) => <span className="font-mono text-[12px] text-muted">{qty(r.book) || "0"}</span>,
    },
    {
      key: "counted",
      header: "Counted",
      width: "100px",
      align: "right",
      value: (r) => parseQty(counted[r.item.id] ?? "") ?? counted[r.item.id] ?? "",
      text: (r) => counted[r.item.id] ?? "",
      cell: (r) => <span className={cx("font-mono text-[12px]", !counted[r.item.id] && "text-faint")}>{counted[r.item.id] || "—"}</span>,
      edit: { get: (r) => counted[r.item.id] ?? "", save: (r, text) => setCounted((c) => ({ ...c, [r.item.id]: text.trim() })) },
    },
    {
      key: "diff",
      header: "Difference",
      width: "100px",
      align: "right",
      value: (r) => diffOf(r),
      cell: (r) => {
        const diff = diffOf(r);
        return (
          <span className={cx("font-mono text-[12px]", diff == null ? "text-faint" : diff < 0 ? "text-danger" : diff > 0 ? "text-ok" : "text-muted")}>
            {diff == null ? "" : diff === 0 ? "±0" : `${diff > 0 ? "+" : "−"}${qty(Math.abs(diff))}`}
          </span>
        );
      },
    },
    // The order they're listed in (the CM's stock, then parts added): what it's sorted by until someone sorts it another way.
    { key: "listed", header: "#", optional: true, width: "48px", align: "right", value: (r) => r.listed + 1 },
  ];

  const submit = async () => {
    const lines = rows.flatMap((r) => {
      const n = parseQty(counted[r.item.id] ?? "");
      return n == null ? [] : [{ item_id: r.item.id, counted: n }];
    });
    if (!lines.length) return toast.error("Type at least one count; parts left blank weren't counted");
    if (lines.some((l) => l.counted < 0)) return toast.error("Counts are 0 or more");
    setBusy(true);
    const { ok, result } = await mutateWithResult("cm_count.submit", {
      id,
      location_id: cm.location.id,
      counted_at: new Date(at).toISOString(),
      make_order_id: build || undefined,
      notes: notes.trim(),
      lines,
      post: true,
    });
    setBusy(false);
    if (!ok) return;
    const r = result as { number: string; posted?: boolean; moves?: number } & PostedText;
    if (!r.posted) return setAsking(true);
    postedToasts(r, cm.location.name);
    onClose();
  };

  if (asking && saved)
    return (
      <Modal
        title={`${saved.number}: one thing to place before it posts`}
        onClose={onClose}
        width={820}
        footer={
          <>
            <span className="text-[12px] text-muted">
              It's saved. Answer each question and post it; or close this and post it later from Consignment → Counts.
            </span>
            <Button variant="ghost" className="ml-auto" onClick={onClose}>
              Later
            </Button>
          </>
        }
      >
        <CountReview count={saved} onPosted={onClose} />
      </Modal>
    );

  return (
    <Modal
      title={`Enter ${cm.location.name}'s count`}
      onClose={onClose}
      width={820}
      footer={
        <>
          <span className="text-[12px] text-muted">
            Parts left blank weren't counted. Posting sets the stock at {cm.location.name} to these numbers; a big difference is flagged to count again.
          </span>
          <Button variant="ghost" className="ml-auto" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" disabled={busy} onClick={submit}>
            Post the count
          </Button>
        </>
      }
    >
      <div className="flex flex-wrap items-center gap-2 border-b border-line px-4 py-2.5">
        <label className="flex items-center gap-1.5 text-[12px] text-muted">
          Counted
          <Input type="datetime-local" value={at} onChange={(e) => setAt(e.target.value)} className="h-7 w-48 text-[12px]" />
        </label>
        {builds.length > 0 && (
          <div className="w-56 shrink-0">
            <Select
              aria-label="After build"
              value={build}
              onChange={(e) => setBuild(e.target.value)}
              options={[
                { value: "", label: "Not after a build" },
                ...builds.map((o) => ({ value: o.mo.id, label: `After ${o.po.number} ${o.item?.sku ?? ""}` })),
              ]}
              className="h-7 text-[12px]"
            />
          </div>
        )}
        <ItemPicker
          className="ml-auto w-64"
          allow={(i) => i.kind !== "service" && !rows.some((r) => r.item.id === i.id)}
          onPick={(item) => setRows((rs) => [...rs, { item, book: 0, inShopify: item.source === "shopify" }])}
          placeholder="Add a part they counted…"
        />
        <div ref={setSlot} className="flex shrink-0 items-center gap-1" />
      </div>
      <div className="px-4 pt-2.5">
        <DataTable
          fit={14}
          rows={entryRows}
          columns={entryColumns}
          editing
          defaultSort={{ key: "listed" }}
          rowNoun={["part", "parts"]}
          exportName="Count entry"
          viewsSlot={slot}
          pageFilters={false}
          empty={`Nothing on the books at ${cm.location.name}. Add the parts they counted.`}
        />
      </div>
      <div className="p-4">
        <Input value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Notes (who counted, how)" aria-label="Notes" />
        <div className="mt-1.5 text-[11px] text-faint">
          The variance is worked out against the book as of the time counted (moves since then don't count), less what was still on its way there.
        </div>
      </div>
    </Modal>
  );
}

type PostedText = { number: string; moves?: number; adjust_in_shopify?: string[]; count_again?: string[] };

/** What posting a count did: the parts changed, Shopify items to adjust there, and big differences to count again. */
function postedToasts(r: PostedText, where: string) {
  toast.ok(`${r.number} posted: ${r.moves ? `${r.moves} ${r.moves === 1 ? "part" : "parts"} at ${where} changed to the count` : "everything matched"}`);
  if (r.adjust_in_shopify?.length) toast.info(`Adjust these Shopify items in Shopify: ${r.adjust_in_shopify.join(", ")}`);
  if (r.count_again?.length) toast.info(`Big differences, worth counting again: ${r.count_again.join(", ")}`);
}

type ReviewRow = ReturnType<typeof reviewCount>["lines"][number] & { id: string; item: Item | undefined; cost: number | null };

const OUTCOME_NOTES = {
  adjusted: "",
  shopify: "adjust in Shopify",
  awaiting_delivery: "waiting to be delivered: receiving settles it",
  superseded: "a later count settled it",
} as const;

/**
 * A count saved but not posted yet (something around it to place, or one entered before counts posted at once).
 * Anything recorded after the count that may or may not be in it is listed to place before or after; the differences
 * (what posting changes) follow those answers at once.
 */
export function CountReview({ count, onPosted }: { count: CmCount; onPosted?: () => void }) {
  const allLines = useRows("cm_count_line");
  const items = useRows("item");
  const users = useRows("app_user");
  const moves = useRows("stock_move");
  const shipments = useRows("consign_shipment");
  const shipmentLines = useRows("consign_shipment_line");
  const counts = useRows("cm_count");
  const makeOrders = useRows("make_order");
  const pos = useRows("po");
  const poLines = useRows("po_line");
  const reports = useRows("cm_report");
  const receipts = useRows("receipt");
  const [decisions, setDecisions] = useState<Record<string, CountDecision>>({});
  const [busy, setBusy] = useState(false);
  const lines = useMemo(() => allLines.filter((l) => l.count_id === count.id), [allLines, count.id]);
  const itemById = useMemo(() => new Map(items.map((i) => [i.id, i])), [items]);
  // The same review the server runs when approving (shared/consignment.ts), over the synced rows.
  const data = useMemo(() => {
    const ids = new Set(lines.map((l) => l.item_id));
    return {
      count,
      lines,
      moves: moves.filter((m) => ids.has(m.item_id) && (m.from_location_id === count.location_id || m.to_location_id === count.location_id)),
      shipments,
      shipmentLines,
      counts,
      makeOrders,
      pos,
      poLines,
      reports,
      receipts,
      items,
    };
  }, [count, lines, moves, shipments, shipmentLines, counts, makeOrders, pos, poLines, reports, receipts, items]);
  const review = useMemo(() => reviewCount(data, decisions), [data, decisions]);
  const costOf = new Map(lines.map((l) => [l.item_id, l.unit_cost]));
  const shown = review.lines.filter((l) => l.variance !== 0 || l.outcome !== "adjusted");
  const off = review.lines.filter((l) => l.variance !== 0 && l.outcome === "adjusted");
  const value = off.reduce((n, l) => n + (costOf.get(l.item_id) == null ? 0 : l.variance * Number(costOf.get(l.item_id))), 0);
  const sku = (id: string) => itemById.get(id)?.sku ?? "?";
  // The lines that matter (a difference, or left alone and why): the usual table, its menus in the count's header.
  const [slot, setSlot] = useState<HTMLDivElement | null>(null);
  const reviewRows: ReviewRow[] = shown.map((l) => {
    const cost = costOf.get(l.item_id);
    return { ...l, id: l.item_id, item: itemById.get(l.item_id), cost: cost == null ? null : Number(cost) };
  });
  const reviewColumns: Column<ReviewRow>[] = [
    {
      key: "sku",
      header: "SKU",
      width: "130px",
      value: (l) => l.item?.sku ?? "",
      cell: (l) => <span className="truncate font-mono text-[12px]">{l.item?.sku}</span>,
    },
    {
      key: "name",
      header: "Part",
      width: "minmax(180px,1fr)",
      value: (l) => l.item?.name ?? "",
      cell: (l) => (
        <span className="flex min-w-0 items-center gap-1.5">
          <span className="truncate">{l.item?.name}</span>
          {l.outcome !== "adjusted" && <Badge>{OUTCOME_NOTES[l.outcome]}</Badge>}
          {l.outcome === "adjusted" && countAgain({ book: l.book, variance: l.variance, unitCost: l.cost }) && <Badge tone="warn">Count again</Badge>}
        </span>
      ),
    },
    {
      key: "book",
      header: "Book",
      width: "90px",
      align: "right",
      value: (l) => l.book,
      cell: (l) => <span className="font-mono text-[12px] text-muted">{qty(l.book) || "0"}</span>,
    },
    {
      key: "counted",
      header: "Counted",
      width: "90px",
      align: "right",
      value: (l) => l.counted,
      cell: (l) => <span className="font-mono text-[12px]">{qty(l.counted) || "0"}</span>,
    },
    {
      key: "variance",
      header: "Difference",
      width: "100px",
      align: "right",
      value: (l) => l.variance,
      cell: (l) => (
        <span
          className={cx(
            "font-mono text-[12px]",
            l.outcome !== "adjusted" ? "text-faint" : l.variance < 0 ? "text-danger" : l.variance > 0 ? "text-ok" : "text-muted",
          )}
        >
          {l.variance === 0 ? "±0" : `${l.variance > 0 ? "+" : "−"}${qty(Math.abs(l.variance))}`}
        </span>
      ),
    },
    {
      key: "value",
      header: "Value",
      width: "100px",
      align: "right",
      value: (l) => (l.cost == null || l.variance === 0 ? null : Math.round(l.variance * l.cost * 100) / 100),
      cell: (l) => (
        <span className="font-mono text-[12px] text-muted">
          {l.cost == null || l.variance === 0 ? "" : `${l.variance > 0 ? "+" : "−"}${money(Math.abs(Math.round(l.variance * l.cost * 100) / 100))}`}
        </span>
      ),
    },
  ];

  const approve = async () => {
    setBusy(true);
    const { ok, result } = await mutateWithResult("cm_count.approve", { id: count.id, decisions });
    setBusy(false);
    if (!ok) return;
    postedToasts({ ...(result as PostedText), number: count.number }, "the CM");
    onPosted?.();
  };
  const reject = async () => {
    const why = prompt(`Why throw ${count.number} away?`);
    if (!why?.trim()) return;
    setBusy(true);
    await mutateWithResult("cm_count.reject", { id: count.id, notes: why.trim() });
    setBusy(false);
  };

  return (
    <div className="border-b border-line last:border-0">
      <div className="flex items-center gap-3 px-4 py-2">
        <ClipboardCheck className="size-4 shrink-0 text-faint" />
        <span className="w-20 shrink-0 font-mono text-[12px] font-medium">{count.number}</span>
        <span className="min-w-0 flex-1 truncate text-[12px] text-muted">
          Counted {new Date(count.counted_at).toLocaleString()} · entered {ago(count.created_at)}
          {` by ${personName(users, count.entered_by)}`}
          {count.notes ? ` · ${count.notes}` : ""}
        </span>
        <span className="shrink-0 text-[12px]">
          {lines.length} counted · {off.length ? <span className="text-warn">{off.length} off</span> : "all match"}
          {off.length > 0 && ` · ${value >= 0 ? "+" : "−"}${money(Math.abs(Math.round(value * 100) / 100))}`}
        </span>
        {shown.length > 0 && <div ref={setSlot} className="flex shrink-0 items-center gap-1" />}
        {can("cm_count.approve") && (
          <>
            <Button size="sm" variant="ghost" disabled={busy} onClick={reject}>
              Throw away
            </Button>
            <Button
              size="sm"
              variant="primary"
              disabled={busy || review.undecided.length > 0}
              onClick={approve}
              title={review.undecided.length ? "Answer each question below first" : "Sets each counted part at the CM to the count (a count move per part)"}
            >
              Post the count
            </Button>
          </>
        )}
      </div>
      {review.questions.length > 0 && (
        <div className="mx-4 mb-2 rounded-md border border-warn/40 bg-warn-soft/40 px-2 py-1.5">
          <div className="pb-1 text-[12px] text-muted">
            Recorded around the count: did each happen before the CM counted (so it's in their numbers) or after?
          </div>
          {review.questions.map((q) => (
            <div key={q.key} className="flex items-center gap-2 py-0.5 text-[12px]">
              <span className="w-24 shrink-0 text-faint">{ago(q.at)}</span>
              <span className="min-w-0 flex-1 truncate" title={q.label}>
                {q.label}
                <span className="ml-1.5 font-mono text-muted">
                  {q.effects.map((e) => `${sku(e.item_id)} ${e.qty > 0 ? "+" : "−"}${qty(Math.abs(e.qty))}`).join(", ")}
                </span>
              </span>
              {(["before", "after"] as const).map((d) => (
                <button
                  key={d}
                  type="button"
                  onClick={() => setDecisions((all) => ({ ...all, [q.key]: d }))}
                  className={cx(
                    "h-6 rounded border px-2 text-[11px]",
                    decisions[q.key] === d ? "border-accent bg-accent-soft text-accent-text" : "border-line text-muted hover:bg-hover",
                  )}
                >
                  {d === "before" ? "Before the count" : "After it"}
                </button>
              ))}
            </div>
          ))}
        </div>
      )}
      {shown.length > 0 && (
        <div className="mx-4 mb-2">
          <DataTable fit rows={reviewRows} columns={reviewColumns} rowNoun={["part", "parts"]} exportName="Count lines" viewsSlot={slot} pageFilters={false} />
        </div>
      )}
    </div>
  );
}
